// Shared helpers for the division scoresheet PDFs.
import { jsPDF } from 'jspdf';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';

export interface ScoresheetTeam {
  seed: number; // 1-indexed
  label: string; // team display name (used for team sports)
  players: string[]; // full player names (used for individual/doubles sports)
}

export interface ScoresheetData {
  division: any;
  teams: any[]; // divisionTeams, loaded with ?include=players
  leagueName?: string;
}

// Turn the raw divisionTeams into ordered seeds with player names.
export function extractTeams(teams: any[]): ScoresheetTeam[] {
  return (teams || []).map((t, i) => {
    const players = (t?.players || [])
      .map((p: any) => {
        if (p?.name) return p.name;
        const fn = p?.info?.first_name || '';
        const ln = p?.info?.last_name || '';
        return `${fn} ${ln}`.trim();
      })
      .filter(Boolean);
    return {
      seed: i + 1,
      label: getTeamDisplayName(t) || t?.name || t?.teamName || 'TBD',
      players,
    };
  });
}

// Most teams show 2 players (doubles). Cap the player columns at the widest team
// (max 2 for the sample layout; single team-name column when there are no players).
export function maxPlayers(teams: ScoresheetTeam[]): number {
  return Math.min(2, teams.reduce((m, t) => Math.max(m, t.players.length), 0));
}

// A team's cell label for grids: player names joined, else the team label.
export function teamCellLabel(t: ScoresheetTeam): string {
  if (t.players.length) return t.players.join(' / ');
  return t.label;
}

export const PAGE_MARGIN = 40;

// Title + subtitle + a row of blank write-in fields. Returns the next y offset.
// `showDateLine: false` omits the Date/Time/Location write-in row (round-robin
// standard sheets don't use it).
export function drawHeaderBlock(
  doc: jsPDF,
  opts: { title: string; subtitle?: string; numTeams: number; showDateLine?: boolean },
): number {
  const x = PAGE_MARGIN;
  let y = PAGE_MARGIN;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(opts.title || 'Division', x, y);
  y += 18;

  if (opts.subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(110);
    doc.text(opts.subtitle, x, y);
    doc.setTextColor(0);
    y += 16;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Teams: ${opts.numTeams}`, x, y);
  y += 14;
  if (opts.showDateLine !== false) {
    doc.text('Date: ______________     Time: ______________     Location: ______________', x, y);
    y += 16;
  }

  return y;
}

// A "Notes:" write-in section pinned to the bottom of the CURRENT page:
// bold label + three ruled lines. Callers reserve the bottom ~100pt (autotable
// margin.bottom) so flowing tables never overlap it.
export function drawNotesSection(doc: jsPDF): void {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const x = PAGE_MARGIN;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.text('Notes:', x, pageH - 92);

  doc.setDrawColor(180);
  doc.setLineWidth(0.5);
  [pageH - 76, pageH - 58, pageH - 40].forEach((y) => {
    doc.line(x, y, pageW - PAGE_MARGIN, y);
  });
  doc.setDrawColor(0);
  doc.setFont('helvetica', 'normal');
}

// Trigger the browser download.
export function saveDoc(doc: jsPDF, filename: string): void {
  doc.save(filename);
}

export function scoresheetFilename(divisionName: string, formatLabel: string): string {
  const clean = (divisionName || 'Division').replace(/[^\w\- ]+/g, '').trim() || 'Division';
  return `${clean} - ${formatLabel} Scoresheet.pdf`;
}
